import React, {useEffect, useState} from 'react';
import BeerIndexWall from './BeerIndexWall';
import BeerIndexWallVertical from './BeerIndexWallVertical';

type ViewMode = 'portrait' | 'vertical';

const VIEW_MODE_KEY = 'beerIndexWall.viewMode';
// Project's own theme accent (see BeerIndexWall/BeerIndexWallVertical DEFAULT_ACCENT), not the
// imported design's orange.
const ACCENT = '#6c5dd3';

const isViewMode = (value: string | null): value is ViewMode => value === 'portrait' || value === 'vertical';

const readStoredViewMode = (): ViewMode | null => {
    try {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        return isViewMode(stored) ? stored : null;
    } catch {
        return null;
    }
};

/**
 * The /bar wall has two visual variants (landscape "portrait view" and tall "vertical view") for
 * TVs mounted in different orientations. Since the same URL serves both, the operator picks one on
 * load; the choice is remembered in localStorage for the rest of that browser session, then cleared
 * on page close so the next time the page is opened (e.g. after a TV reboot) it prompts again rather
 * than silently keeping a stale choice from whoever set it up last.
 */
const BeerIndexWallGate = () => {
    const [viewMode, setViewMode] = useState<ViewMode | null>(readStoredViewMode);

    useEffect(() => {
        const clearStoredChoice = () => {
            try {
                localStorage.removeItem(VIEW_MODE_KEY);
            } catch {
                // ignore
            }
        };
        // pagehide covers mobile/kiosk browsers that don't reliably fire beforeunload.
        window.addEventListener('beforeunload', clearStoredChoice);
        window.addEventListener('pagehide', clearStoredChoice);
        return () => {
            window.removeEventListener('beforeunload', clearStoredChoice);
            window.removeEventListener('pagehide', clearStoredChoice);
        };
    }, []);

    const selectView = (mode: ViewMode) => {
        try {
            localStorage.setItem(VIEW_MODE_KEY, mode);
        } catch {
            // ignore
        }
        setViewMode(mode);
    };

    if (viewMode === 'portrait') return <BeerIndexWall/>;
    if (viewMode === 'vertical') return <BeerIndexWallVertical/>;
    return <ViewModePrompt onSelect={selectView}/>;
};

const ViewModePrompt = ({onSelect}: { onSelect: (mode: ViewMode) => void }) => (
    <div style={{
        position: 'fixed', inset: 0, background: '#05070a', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 40, fontFamily: "'Poppins',sans-serif", color: '#fff',
        padding: 24, boxSizing: 'border-box',
    }}>
        <div style={{textAlign: 'center'}}>
            <div style={{fontWeight: 900, fontSize: 30, letterSpacing: '.01em'}}>
                BAR<span style={{color: ACCENT}}>VICTORY</span>
            </div>
            <div style={{fontFamily: "'Poppins',sans-serif", fontSize: 13, letterSpacing: '.16em', color: 'rgba(255,255,255,.45)', marginTop: 10}}>
                ALEGE MODUL DE AFISARE PENTRU ACEST ECRAN
            </div>
        </div>
        <div style={{display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center'}}>
            <PromptOption
                title={'Vedere Portret'}
                subtitle={'Ecran orizontal, 1920 x 1080'}
                onClick={() => onSelect('portrait')}
            />
            <PromptOption
                title={'Vedere Verticala'}
                subtitle={'Ecran vertical, 1080 x 1920'}
                onClick={() => onSelect('vertical')}
            />
        </div>
    </div>
);

const PromptOption = ({title, subtitle, onClick}: { title: string, subtitle: string, onClick: () => void }) => (
    <button
        type={'button'}
        onClick={onClick}
        style={{
            width: 260, padding: '30px 24px', borderRadius: 16, cursor: 'pointer',
            background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', color: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center',
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = `${ACCENT}1f`;
            e.currentTarget.style.borderColor = `${ACCENT}66`;
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,.04)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)';
        }}
    >
        <span style={{fontWeight: 800, fontSize: 20, textTransform: 'uppercase'}}>{title}</span>
        <span style={{fontFamily: "'Poppins',sans-serif", fontSize: 12, letterSpacing: '.1em', color: 'rgba(255,255,255,.45)'}}>{subtitle}</span>
    </button>
);

export default BeerIndexWallGate;
